import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Image as ImageIcon, Send, X, Loader2, Zap, ChevronDown, ChevronUp, Video, XCircle, Smile } from 'lucide-react';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import { UserProfile, Player, FlashBanner } from '../../types';
import { collection, addDoc, query, where, onSnapshot, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, getCollectionPath, storage } from '../../lib/firebase';
import { compressImage } from '../../lib/imageCompression';
import { ALL_CITIES } from '../../data/locations';
import { MentionInput } from './MentionInput';
import { FadeInImage } from '../common/FadeInImage';

interface PostCreatorProps {
  userProfile: UserProfile;
  primaryColor: string;
  currentPlayer?: Player;
  clubProfile?: UserProfile;
  selectedCity?: string;
}

export function PostCreator({ userProfile, primaryColor, currentPlayer, clubProfile, selectedCity }: PostCreatorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [postType, setPostType] = useState<'social' | 'flash_banner'>('social');
  const [content, setContent] = useState('');
  const [postAs, setPostAs] = useState<'player' | 'club'>(clubProfile ? 'club' : 'player');
  
  // Dummy data for selections
  const categories = ['1ra', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va'];
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // States for active Flash Banner
  const [activeBanner, setActiveBanner] = useState<FlashBanner | null>(null);
  const [isDeletingBanner, setIsDeletingBanner] = useState(false);
  const [showRules, setShowRules] = useState(false);

  // Multimedia States
  const [images, setImages] = useState<File[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state when identity profiles change (e.g. when role changes)
  useEffect(() => {
    if (!clubProfile && postAs === 'club') {
      setPostAs('player');
      if (postType === 'flash_banner') {
        setPostType('social');
      }
    } else if (!currentPlayer && clubProfile && postAs === 'player') {
      setPostAs('club');
    }
  }, [clubProfile, currentPlayer, postAs, postType]);

  // Query active flash banner when posting as club
  useEffect(() => {
    if (postAs !== 'club' || !clubProfile?.uid) {
      setActiveBanner(null);
      return;
    }

    const q = query(
      collection(db, 'flash_banners'),
      where('clubId', '==', clubProfile.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const banners = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FlashBanner[];
      
      // Filter client-side for banners that are active (either superadmin or created in the last 2 hours)
      const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000;
      const activeBanners = banners.filter(b => b.createdBySuperAdmin || b.createdAt > twoHoursAgo);
      
      if (activeBanners.length > 0) {
        // Sort by createdAt desc to get the most recent one
        activeBanners.sort((a, b) => b.createdAt - a.createdAt);
        setActiveBanner(activeBanners[0]);
      } else {
        setActiveBanner(null);
      }
    }, (error) => {
      console.error("Error fetching flash banners:", error);
    });

    return () => unsubscribe();
  }, [postAs, clubProfile?.uid]);

  const toggleCat = (cat: string) => {
    setSelectedCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleDeleteActiveBanner = async () => {
    if (!activeBanner) return;
    if (!await window.confirm('¿Estás seguro de que deseas eliminar este Banner Flash?')) return;
    
    setIsDeletingBanner(true);
    try {
      await deleteDoc(doc(db, 'flash_banners', activeBanner.id));
      setActiveBanner(null);
    } catch (err) {
      console.error('Error deleting flash banner:', err);
      alert('No se pudo eliminar el banner.');
    } finally {
      setIsDeletingBanner(false);
    }
  };

  const handlePublish = async () => {
    if (!content.trim() || isPublishing) return;
    
    setIsPublishing(true);

    try {
      const isPostingAsClub = postAs === 'club' && clubProfile;
      const authorId = isPostingAsClub ? clubProfile.uid : (currentPlayer?.id || userProfile.uid);
      const authorName = isPostingAsClub ? (clubProfile.name || 'Sede') : (currentPlayer?.name || userProfile.name || 'Usuario');
      const authorPhotoUrl = isPostingAsClub ? (clubProfile.photoUrl || '') : (currentPlayer?.photoUrl || userProfile.photoUrl || '');
      const authorRole = isPostingAsClub 
        ? (userProfile.role === 'superadmin' ? 'superadmin' : 'admin') 
        : 'player';
      const isPriority = Boolean(isPostingAsClub && clubProfile?.subscriptionPlan === 'Full Plus');
      let city = isPostingAsClub ? (clubProfile.city || userProfile.city || selectedCity || ALL_CITIES[0]?.id) : (currentPlayer?.city || userProfile.city || selectedCity || ALL_CITIES[0]?.id);
      if (userProfile.role === 'superadmin') {
        city = selectedCity || 'Todas las ciudades';
      }

      if (postType === 'flash_banner') {
        if (activeBanner) {
          alert('Ya tienes un Banner Flash activo. Debes eliminarlo antes de poder crear otro.');
          return;
        }

        const bannerData = {
          clubId: authorId,
          clubName: authorName,
          clubPhotoUrl: authorPhotoUrl,
          content: content.trim().substring(0, 60),
          createdAt: Date.now(),
          city,
          createdBySuperAdmin: userProfile.role === 'superadmin',
        };

        await addDoc(collection(db, 'flash_banners'), bannerData);
      } else {
        let uploadedImageUrls: string[] = [];
        if (images.length > 0) {
          for (const file of images) {
            try {
              const compressedFile = await compressImage(file);
              const storageRef = ref(storage, `posts/${authorId}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`);
              await uploadBytes(storageRef, compressedFile);
              const url = await getDownloadURL(storageRef);
              uploadedImageUrls.push(url);
            } catch (e) {
              console.error('Error uploading image', e);
            }
          }
        }

        const newPost = {
          authorId,
          authorName,
          authorPhotoUrl,
          authorRole,
          type: postType,
          content: content.trim(),
          imagesUrl: uploadedImageUrls.length > 0 ? uploadedImageUrls : null,
          videoUrl: videoUrl.trim() || null,
          targetCategories: selectedCats.length > 0 ? selectedCats : null,
          targetGender: selectedGender,
          city,
          isPriority,
          likes: [],
          comments: [],
          createdAt: Date.now(),
        };

        const docRef = await addDoc(collection(db, 'posts'), newPost);

        // Notify mentioned users
        const mentions = content.match(/(?:^|\s)(@[^\s]+)/g);
        if (mentions) {
          const processedNames = new Set<string>();
          for (const m of mentions) {
            const mentionName = m.trim().substring(1).replace(/_/g, ' ');
            if (processedNames.has(mentionName)) continue;
            processedNames.add(mentionName);
            
            const userQuery = query(collection(db, 'users'), where('name', '==', mentionName));
            const snaps = await getDocs(userQuery);
            snaps.forEach(async (docSnap) => {
              if (docSnap.id !== userProfile.uid) {
                await addDoc(collection(db, getCollectionPath('notifications')), {
                  userId: docSnap.id,
                  type: 'post_mention',
                  title: 'Te han mencionado',
                  body: `${authorName} te mencionó en una publicación.`,
                  postId: docRef.id,
                  read: false,
                  createdAt: Date.now()
                });
              }
            });
          }
        }
      }
      
      // Reset form
      setContent('');
      setPostType('social');
      setIsExpanded(false);
      setImages([]);
      setVideoUrl('');
      setShowVideoInput(false);
    } catch (error) {
      console.error('Error publishing:', error);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className={`w-full bg-slate-900/60 rounded-[1.5rem] border backdrop-blur-md transition-all duration-300 ${isExpanded ? 'border-theme-primary/40 shadow-[0_0_30px_rgba(var(--glow-rgb),0.1)]' : 'border-white/10 hover:border-white/20'}`}>
      
      {!isExpanded ? (
        <div 
          onClick={() => setIsExpanded(true)}
          className="p-4 flex items-center gap-3 cursor-text"
        >
          <div className="w-10 h-10 rounded-full bg-theme-primary/20 flex items-center justify-center border border-theme-primary/30 shrink-0 overflow-hidden">
            {postAs === 'club' && clubProfile?.photoUrl ? (
              <FadeInImage src={clubProfile.photoUrl} alt="Club" className="w-full h-full object-cover" />
            ) : currentPlayer?.photoUrl || userProfile.photoUrl ? (
              <FadeInImage src={currentPlayer?.photoUrl || userProfile.photoUrl} alt="User" className="w-full h-full object-cover" />
            ) : (
              <MessageCircle size={18} className="theme-primary-text" />
            )}
          </div>
          <div className="flex-1 bg-white/5 rounded-xl px-4 py-2.5 text-sm text-white/40 font-medium border border-white/5">
            ¿Qué te gustaría publicar?
          </div>
        </div>
      ) : (
        <div className="p-5 flex flex-col gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex justify-between items-center mb-2">
            <div className="flex gap-2 p-1 bg-black/40 rounded-lg">
              <button
                onClick={() => setPostType('social')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${postType === 'social' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/80'}`}
              >
                Social
              </button>

              {postAs === 'club' && (
                <button
                  onClick={() => {
                    setPostType('flash_banner');
                    setContent(prev => prev.substring(0, 65));
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${postType === 'flash_banner' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-white/40 hover:text-white/80 border border-transparent'}`}
                >
                  Banner Flash
                </button>
              )}
            </div>
            <button onClick={() => setIsExpanded(false)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-all">
              <X size={16} />
            </button>
          </div>

          {currentPlayer && clubProfile && (
            <div className="flex items-center gap-3 mb-2 bg-black/20 p-2 rounded-xl border border-white/5">
              <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold ml-2">Publicar como:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setPostAs('player');
                    if (postType === 'flash_banner') {
                      setPostType('social');
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${postAs === 'player' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/80'}`}
                >
                  <div className="w-5 h-5 rounded-full overflow-hidden bg-slate-800 shrink-0">
                    {currentPlayer.photoUrl ? <FadeInImage src={currentPlayer.photoUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-theme-primary/20" />}
                  </div>
                  Jugador
                </button>
                <button
                  onClick={() => setPostAs('club')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${postAs === 'club' ? 'bg-theme-primary/20 theme-primary-text' : 'text-white/40 hover:text-white/80'}`}
                >
                  <div className="w-5 h-5 rounded-full overflow-hidden bg-slate-800 shrink-0">
                    {clubProfile.photoUrl ? <FadeInImage src={clubProfile.photoUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-theme-primary/20" />}
                  </div>
                  Club
                </button>
              </div>
            </div>
          )}

          {postType === 'flash_banner' && activeBanner ? (
            <div className="flex flex-col gap-4 p-4 bg-purple-950/20 border border-purple-500/20 rounded-2xl animate-in fade-in duration-300">
              <div className="flex items-center justify-between border-b border-purple-500/10 pb-2.5">
                <span className="text-[10px] font-black uppercase text-purple-300 tracking-widest flex items-center gap-1.5">
                  <Zap size={12} className="text-amber-400 fill-amber-400 animate-pulse" /> Tu Banner Flash Activo
                </span>
                <span className="text-[10px] text-purple-300 font-bold bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                  Activo
                </span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-black/40 border border-white/5 rounded-xl">
                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-white/10">
                  {clubProfile?.photoUrl ? (
                    <FadeInImage src={clubProfile.photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300">
                      {clubProfile?.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-white/50">{clubProfile?.name}</span>
                    <span className="text-[9px] font-black uppercase tracking-wider text-amber-400 bg-amber-400/10 px-1 rounded">⚡ Flash</span>
                  </div>
                  <p className="text-xs text-white/90 font-medium truncate mt-0.5">{activeBanner.content}</p>
                </div>
              </div>

              <div className="text-[10px] text-zinc-400 font-medium leading-relaxed">
                ⚠️ Solo puedes tener 1 Banner Flash activo a la vez. Para publicar uno nuevo, debes eliminar el banner actual.
              </div>

              <button
                onClick={handleDeleteActiveBanner}
                disabled={isDeletingBanner}
                className="w-full py-2.5 rounded-xl border border-red-500/30 bg-red-500/10 hover:bg-red-500/25 text-red-300 font-black uppercase tracking-widest text-[11px] transition-all flex items-center justify-center gap-2"
              >
                {isDeletingBanner ? 'Eliminando...' : 'Eliminar Banner Activo'}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2">
                <div className="relative pt-1 shrink-0">
                  <button 
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className={`p-1.5 rounded-full transition-colors ${showEmojiPicker ? 'text-amber-400 bg-amber-400/10' : 'text-white/40 hover:bg-white/10 hover:text-white'}`} 
                    title="Añadir emoticones"
                  >
                    <Smile size={20} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute top-full left-0 mt-2 z-50">
                      <div className="fixed inset-0" onClick={() => setShowEmojiPicker(false)} />
                      <div 
                        className="relative shadow-2xl rounded-xl overflow-hidden border border-white/10 bg-[#222222]" 
                        style={{ width: '210px', height: '245px' }}
                      >
                        <div style={{ transform: 'scale(0.7)', transformOrigin: 'top left', width: '300px', height: '350px' }}>
                          <EmojiPicker 
                            width="100%"
                            height="100%"
                            emojiStyle={EmojiStyle.NATIVE}
                            theme={Theme.DARK}
                            previewConfig={{ showPreview: false }}
                            onEmojiClick={(emojiData) => {
                              setContent(prev => prev + emojiData.emoji);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <MentionInput
                    isTextArea
                    autoFocus
                    dropdownPosition="bottom"
                    selectedCity={selectedCity}
                    value={content}
                    onChange={(val) => {
                      setContent(postType === 'flash_banner' ? val.substring(0, 65) : val);
                    }}
                    maxLength={postType === 'flash_banner' ? 65 : undefined}
                    placeholder={
                      postType === 'matchmaking' 
                        ? 'Ej: Falta 1 para jugar hoy a las 20hs en Club Las Rejas...' 
                        : postType === 'flash_banner' 
                          ? 'Ej: ⚡Falta 1 para las 20hs en pista 3!' 
                          : '¿Qué querés compartir con la comunidad?'
                    }
                    className="w-full bg-transparent border-none outline-none text-white text-sm md:text-base resize-none min-h-[80px] placeholder:text-white/30 pt-2"
                  />
                </div>
              </div>

              {postType === 'flash_banner' && (
                <>
                  <div className="flex justify-between items-center text-[10px] text-zinc-400 font-bold border-t border-white/5 pt-2">
                    <span>Límite de caracteres</span>
                    <span className={content.length >= 60 ? 'text-amber-400' : 'text-purple-300'}>
                      {content.length} / 65
                    </span>
                  </div>

                  <div className="text-[11px] text-zinc-400 font-medium mt-1 p-3 bg-black/20 rounded-xl border border-white/5 flex flex-col gap-1 transition-all duration-300">
                    <button 
                      onClick={() => setShowRules(!showRules)}
                      className="flex items-center justify-between text-white/60 font-bold w-full text-left focus:outline-none hover:text-white transition-colors"
                    >
                      <span className="flex items-center gap-1.5">ℹ️ Reglas del Banner Flash</span>
                      {showRules ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    
                    {showRules && (
                      <div className="flex flex-col gap-1 mt-1.5 pt-1.5 border-t border-white/5 animate-in fade-in duration-200">
                        <span>• El banner aparecerá destacado arriba en la pestaña Comunidad para todos los usuarios de tu ciudad.</span>
                        <span>• Solo puedes publicar 1 banner a la vez. Expirará automáticamente en 2 horas.</span>
                        <span>• Al hacer clic en tu banner, los jugadores abrirán un chat privado directamente contigo.</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {postType !== 'flash_banner' && (
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <span className="text-[10px] font-black uppercase text-white/40 tracking-widest">Etiquetas (Opcional)</span>
              
              <div className="flex flex-col gap-2">
                <div className="flex gap-1.5 flex-wrap">
                  {[...categories].reverse().map(cat => (
                    <button
                      key={cat}
                      onClick={() => toggleCat(cat)}
                      className={`flex-1 min-w-0 text-center px-2 py-1.5 rounded-xl text-[11px] font-bold tracking-wide transition-all duration-200 ${
                        selectedCats.includes(cat) 
                          ? 'bg-theme-primary/25 theme-primary-text border border-theme-primary/50' 
                          : 'bg-white/[0.07] text-white/50 border border-white/[0.1] hover:bg-white/[0.12] hover:text-white/70'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  {[
                    { key: 'Masculino', label: '♂ Masc' },
                    { key: 'Femenino', label: '♀ Fem' },
                    { key: 'Mixto', label: '⚤ Mixto' }
                  ].map(gen => (
                    <button
                      key={gen.key}
                      onClick={() => setSelectedGender(selectedGender === gen.key ? null : gen.key)}
                      className={`px-3 py-1.5 rounded-xl text-[11px] font-semibold tracking-wide transition-all duration-200 ${
                        selectedGender === gen.key 
                          ? 'bg-white/20 text-white border border-white/25' 
                          : 'bg-white/[0.07] text-white/50 border border-white/[0.1] hover:bg-white/[0.12] hover:text-white/70'
                      }`}
                    >
                      {gen.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!(postType === 'flash_banner' && activeBanner) && (
            <div className="flex flex-col gap-3 mt-2 pt-2">
              {images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {images.map((img, i) => (
                    <div key={i} className="relative shrink-0 w-16 h-16 rounded-xl overflow-hidden border border-white/10 group">
                      <img src={URL.createObjectURL(img)} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))} className="p-1 rounded-full bg-black/50 text-white/80 hover:text-white transition-colors">
                          <XCircle size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showVideoInput && (
                <div className="flex items-center">
                  <input 
                    type="text" 
                    placeholder="Enlace de YouTube..."
                    value={videoUrl}
                    onChange={e => setVideoUrl(e.target.value)}
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-theme-primary/50"
                  />
                  <button onClick={() => { setShowVideoInput(false); setVideoUrl(''); }} className="ml-2 p-2 text-white/40 hover:text-white transition-colors">
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center relative">
                <div className="flex items-center gap-1">
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    className="hidden" 
                    ref={fileInputRef} 
                    onChange={(e) => {
                      if (e.target.files) {
                        setImages(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 4));
                      }
                    }} 
                  />
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-2 rounded-full transition-colors ${images.length > 0 ? 'text-theme-primary bg-theme-primary/10' : 'text-white/40 hover:bg-white/10 hover:text-white'}`} 
                    title="Añadir imagen (Max 4)"
                  >
                    <ImageIcon size={20} />
                  </button>
                  <button 
                    onClick={() => setShowVideoInput(!showVideoInput)}
                    className={`p-2 rounded-full transition-colors ${showVideoInput || videoUrl ? 'text-theme-primary bg-theme-primary/10' : 'text-white/40 hover:bg-white/10 hover:text-white'}`} 
                    title="Añadir video externo (Ej: YouTube)"
                  >
                    <Video size={20} />
                  </button>
                </div>
                <button 
                onClick={handlePublish}
                disabled={!content.trim() || isPublishing}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-amber-400 text-black hover:bg-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.25)]"
              >
                {isPublishing ? 'Publicando...' : 'Publicar'} {isPublishing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
